import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Chip,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import Fade from "@mui/material/Fade";

const formatLabel = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase());

export default function FoodRecordsModal({
  open,
  onClose,
  records = [],
  actions = [],
  heading = "Data Records",
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [containerHeight, setContainerHeight] = useState(400);
  const [scrollTop, setScrollTop] = useState(0);
  const scrollContainerRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);


  const isCollectionsView = heading === "Collections Data";



  useEffect(() => {
    const calculateContainerHeight = () => {
      const headerHeight = 64;
      const searchBarHeight = 58;
      const tableHeaderHeight = 48;

      const availableHeight =
        window.innerHeight - headerHeight - searchBarHeight - tableHeaderHeight;

      setContainerHeight(Math.max(300, availableHeight));
    };

    calculateContainerHeight();
    window.addEventListener("resize", calculateContainerHeight);

    return () =>
      window.removeEventListener("resize", calculateContainerHeight);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const getSearchableValues = (input) => {
    const result = [];

    const walk = (value) => {
      if (value == null) return;

      if (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
      ) {
        result.push(String(value));
        return;
      }

      if (Array.isArray(value)) {
        value.forEach(walk);
        return;
      }

      if (typeof value === "object") {
        Object.values(value).forEach(walk);
      }
    };

    walk(input);
    return result;
  };

  const filteredRecords = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();

    if (!query) return records;

    return records.filter((record) => {
      const searchableValues = isCollectionsView
        ? getSearchableValues(record)
        : getSearchableValues({
          ...record,
          values: record.values,
        });

      return searchableValues.some((value) =>
        value.toLowerCase().includes(query)
      );
    });

  }, [records, debouncedSearch, isCollectionsView]);
  const rowHeight = 53;
  const overscan = 50;
  const visibleRowCount = Math.ceil(containerHeight / rowHeight);
  const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const endIndex = Math.min(
    filteredRecords.length,
    startIndex + visibleRowCount + overscan * 2
  );
  const virtualRows = filteredRecords.slice(startIndex, endIndex);
  const topSpacerHeight = startIndex * rowHeight;
  const bottomSpacerHeight =
    (filteredRecords.length - endIndex) * rowHeight;

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
    setScrollTop(0);
  }, [searchTerm]);

  useEffect(() => {
    setSearchTerm("")
  }, [open])


  const visibleKeysForSiteData = React.useMemo(() => {
    const keys = [];
    const addedKeys = new Set();

    for (let i = 0; i < records.length; i += 5) {
      const chunk = records.slice(i, i + 5);

      for (const record of chunk) {
        const values = record?.values || {};

        for (const [key, value] of Object.entries(values)) {

          if (addedKeys.has(key)) continue;
          if (
            value === null ||
            value === undefined ||
            value === ""
          ) {
            continue;
          }
          if (
            typeof value === "string" &&
            value.trim().toLowerCase() ===
            String(record?.identifier || "").trim().toLowerCase()
          ) {
            continue;
          }

          keys.push(key);
          addedKeys.add(key);

          // stop once 3 keys found
          if (keys.length === 2) {
            return keys;
          }
        }
      }
    }

    return keys;
  }, [records]);

  const commaSeparatedValueKeys = React.useMemo(() => {
    if (!visibleKeysForSiteData?.length) return [];

    const result = [];

    // scan only first 5 records
    const limitedRecords = records.slice(0, 5);

    for (const key of visibleKeysForSiteData) {
      let hasCommaSeparatedValue = false;

      for (const record of limitedRecords) {
        const value = record?.values?.[key];

        if (
          typeof value === "string" &&
          value.includes(",")
        ) {
          hasCommaSeparatedValue = true;
          break;
        }
      }

      if (hasCommaSeparatedValue) {
        result.push(key);
      }
    }

    return result;
  }, [records, visibleKeysForSiteData]);


  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      maxWidth={false}
      TransitionComponent={Fade}
      transitionDuration={{
        enter: 500,
        exit: 500,
      }}
      PaperProps={{
        sx: {
          width: "100vw",
          height: "100%",
          margin: 0,
          borderRadius: 0,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          background: "#fff",
        }}
      >
        <Box
          sx={{
            background: "rgb(87,27,46)",
            px: 2.5,
            py: 1.8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 600,
              color: "#fff",
            }}
          >
            {heading}
          </Typography>

          <IconButton
            onClick={onClose}
            sx={{
              width: 30,
              height: 30,
              background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.2)",
              color: "#fff",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            px: 1,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "rgba(90, 28, 47, 0.06)",
            borderBottom: "1px solid rgba(90, 28, 47, 0.15)",
            flexShrink: 0,
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Search records..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      fontSize: 18,
                      color: "rgb(87,27,46)",
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 38,
                borderRadius: "10px",
                background: "rgba(90, 28, 47, 0.03)",
                fontSize: 13,
                "& fieldset": {
                  borderColor: "rgba(90, 28, 47, 0.18)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(90, 28, 47, 0.35)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgb(87,27,46)",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#777",
                opacity: 1,
              },
            }}
          />

          <Chip
            label={filteredRecords.length}
            size="small"
            sx={{
              height: 22,
              fontWeight: 600,
              background: "rgb(87,27,46)",
              color: "#fff",
            }}
          />

          <Typography
            sx={{
              fontSize: 12,
              color: "rgb(87,27,46)",
              fontWeight: 500,
            }}
          >
            records
          </Typography>
        </Box>

        <Box
          ref={scrollContainerRef}
          onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
          sx={{
            flex: 1,
            overflow: "auto",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: isCollectionsView ? "700px" : "900px",
            }}
          >
            <thead
              style={{
                position: "sticky",
                top: 0,
                zIndex: 5,
              }}
            >
              <tr>
                {isCollectionsView ? (
                  <>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Identifiers</th>
                    {actions.length > 0 && (
                      <th style={thStyle}>Actions</th>
                    )}
                  </>
                ) : (
                  <>
                    <th style={thStyle}>Name</th>

                    {visibleKeysForSiteData
                      .map((key) => (
                        <th key={key} style={thStyle}>
                          {formatLabel(key)}
                        </th>
                      ))}

                    {actions.length > 0 && (
                      <th style={thStyle}>Actions</th>
                    )}
                  </>
                )}
              </tr>
            </thead>

            <tbody>

              {topSpacerHeight > 0 && (
                <tr style={{ height: `${topSpacerHeight}px` }}>
                  <td
                    colSpan={
                      isCollectionsView
                        ? actions.length > 0
                          ? 3
                          : 2
                        : 1 +
                        visibleKeysForSiteData.length +
                        (actions.length > 0 ? 1 : 0)
                    }
                    style={{ padding: 0, border: 0 }}
                  />
                </tr>
              )}

              {virtualRows.map((record) => (
                <tr
                  key={record.id}
                  style={{
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                    height: `${rowHeight}px`,
                  }}
                >
                  {isCollectionsView ? (
                    <>
                      <td
                        style={{
                          ...tdStyle,
                          fontWeight: 600,
                          color: "rgb(87,27,46)",
                        }}
                      >
                        {record.name || "—"}
                      </td>

                      <td style={tdStyle}>
                        {Array.isArray(record.dataIds) &&
                          record.dataIds.length > 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              flexWrap: "wrap",
                            }}
                          >
                            {record.dataIds.map((item) => (
                              <Button
                                key={item?.id}
                                disableElevation
                                sx={{
                                  textTransform: "none",
                                  width: "fit-content",
                                  minWidth: "unset",
                                  px: 1.2,
                                  height: 28,
                                  minHeight: 28,
                                  borderRadius: "12px",
                                  fontSize: "0.72rem",
                                  fontWeight: 600,
                                  lineHeight: 1,
                                  color: "#fff",
                                  background:
                                    "linear-gradient(135deg, rgb(58,130,150) 0%, rgb(63,88,111) 100%)",
                                  boxShadow: "0 2px 8px rgba(63,88,111,0.18)",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    background:
                                      "linear-gradient(135deg, rgb(58,130,150) 0%, rgb(63,88,111) 100%)",
                                    transform: "translateY(-1px)",
                                    boxShadow: "0 4px 12px rgba(63,88,111,0.25)",
                                  },
                                  "&:active": {
                                    transform: "scale(0.98)",
                                  },
                                }}
                              >
                                {item?.identifier}
                              </Button>
                            ))}
                          </Box>
                        ) : (
                          <span
                            style={{
                              color: "#999",
                              fontStyle: "italic",
                            }}
                          >
                            —
                          </span>
                        )}
                      </td>

                      {actions.length > 0 && (
                        <td style={tdStyle}>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            {actions.map((action, index) => (
                              <IconButton
                                key={`${action.name}-${index}`}
                                size="small"
                                title={action.name}
                                onClick={() => action.onClick(record)}
                                sx={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: "8px",
                                  border:
                                    index === 0
                                      ? "1px solid rgba(87,27,46, 0.2)"
                                      : "1px solid rgb(58,130,150)",
                                  color:
                                    index === 0
                                      ? "rgb(87,27,46)"
                                      : "rgb(58,130,150)",
                                  background: "#fff",
                                  "&:hover": {
                                    backgroundColor:
                                      index === 0
                                        ? "rgba(87,27,46, 0.08)"
                                        : "rgba(58,130,150,0.08)",
                                  },
                                }}
                              >
                                {action.icon}
                              </IconButton>
                            ))}
                          </Box>
                        </td>
                      )}
                    </>
                  ) : (
                    <>
                      <td
                        style={{
                          ...tdStyle,
                          fontWeight: 600,
                          color: "rgb(87,27,46)",
                        }}
                      >
                        {record.identifier || "—"}
                      </td>


                      {visibleKeysForSiteData.map((key) => {
                        const value = record.values?.[key];
                        const isCommaSeparated =
                          commaSeparatedValueKeys.includes(key);

                        return (
                          <td key={key} style={tdStyle}>
                            {value === null ||
                              value === undefined ||
                              value === "" ? (
                              <span
                                style={{
                                  color: "#999",
                                  fontStyle: "italic",
                                }}
                              >
                                —
                              </span>
                            ) : isCommaSeparated ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.5,
                                  flexWrap: "wrap",
                                }}
                              >
                                {String(value)
                                  .split(",")
                                  .map((item) => item.trim())
                                  .filter(Boolean)
                                  .map((item) => (
                                    <Button
                                      key={item}
                                      disableElevation
                                      sx={{
                                        textTransform: "none",
                                        width: "fit-content",
                                        minWidth: "unset",
                                        px: 1.2,
                                        height: 28,
                                        minHeight: 28,
                                        borderRadius: "12px",
                                        fontSize: "0.72rem",
                                        fontWeight: 600,
                                        lineHeight: 1,
                                        color: "#fff",
                                        background:
                                          "linear-gradient(135deg, rgb(58,130,150) 0%, rgb(63,88,111) 100%)",
                                        boxShadow:
                                          "0 2px 8px rgba(63,88,111,0.18)",
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                          background:
                                            "linear-gradient(135deg, rgb(58,130,150) 0%, rgb(63,88,111) 100%)",
                                          transform: "translateY(-1px)",
                                          boxShadow:
                                            "0 4px 12px rgba(63,88,111,0.25)",
                                        },
                                        "&:active": {
                                          transform: "scale(0.98)",
                                        },
                                      }}
                                    >
                                      {item.trim()}
                                    </Button>
                                  ))}
                              </Box>
                            ) : (
                              String(value)
                            )}
                          </td>
                        );
                      })}

                      {actions.length > 0 && (
                        <td style={tdStyle}>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            {actions.map((action, index) => (
                              <IconButton
                                key={`${action.name}-${index}`}
                                size="small"
                                title={action.name}
                                onClick={() => action.onClick(record)}
                                sx={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: "8px",
                                  border:
                                    index === 1
                                      ? "1px solid rgba(87,27,46, 0.2)"
                                      : "1px solid rgba(58,130,150,0.2)",
                                  color:
                                    index === 1
                                      ? "error.main"
                                      : "rgb(87,27,46)",
                                  backgroundColor: "#fff",
                                  "&:hover": {
                                    backgroundColor:
                                      index === 1
                                        ? "rgba(87,27,46, 0.08)"
                                        : "rgba(90, 28, 47, 0.06)",
                                  },
                                }}
                              >
                                {action.icon}
                              </IconButton>
                            ))}
                          </Box>
                        </td>
                      )}
                    </>
                  )}
                </tr>
              ))}



              {bottomSpacerHeight > 0 && (
                <tr style={{ height: `${bottomSpacerHeight}px` }}>
                  <td
                    colSpan={
                      isCollectionsView
                        ? actions.length > 0
                          ? 3
                          : 2
                        : 1 +
                        visibleKeysForSiteData.length +
                        (actions.length > 0 ? 1 : 0)
                    }
                    style={{ padding: 0, border: 0 }}
                  />
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      </Box>
    </Dialog>
  );
}

const thStyle = {
  background: "rgb(87,27,46)",
  color: "#fff",
  fontSize: "13px",
  fontWeight: 600,
  padding: "12px 16px",
  textAlign: "left",
  whiteSpace: "nowrap",
  borderRight: "1px solid rgba(255,255,255,0.08)",
};

const tdStyle = {
  padding: "12px 16px",
  fontSize: "13px",
  color: "#222",
  whiteSpace: "nowrap",
};

const btnStyle = (disabled) => ({
  padding: "6px 12px",
  borderRadius: "8px",
  border: "1px solid rgba(0,0,0,0.1)",
  background: disabled ? "#eee" : "#fff",
  cursor: disabled ? "not-allowed" : "pointer",
});